import { memo, useEffect, useMemo, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { creerClient } from '../../api/client.js';
import type { Session } from '../../api/contrat.js';
import { Lien, useNaviguer } from '../../app/navigation.js';
import { accueilPour, roleEffectif } from '../../app/roles.js';
import { COULEURS as UI, ESPACEMENTS } from '../../../../../packages/ui/src/tokens/tokens.js';
import { CATALOGUE_DEMARRAGE } from '../demarrage/catalogue.js';
import { creerControleurDemarrage } from '../demarrage/controleur.js';
import { AvertissementForcage } from '../demarrage/forcage.js';
import { evaluerConditions, manquantes, optionChoisie, CONDITION_MANDAT_RECHERCHE } from '../demarrage/modele.js';
import { choisirModeTravail, useModeTravail, type ModeTravail } from './mode.js';

/**
 * Couloirs G, points 1 et 2 : ce que la coquille ajoute autour de l'écran courant, pour Isabelle et Brigitte.
 * - À l'accueil, tant qu'aucun mode n'est choisi : les deux grands choix « Mode guidé » et « Mode avancé ».
 * - En mode guidé, l'accueil ouvre le poste de pilotage guidé (`/guide`, écran D18).
 * - Un bandeau dit le mode et permet d'en changer, sur tout écran.
 * - Tout écran ouvert depuis le guide (`guide=1` dans l'adresse) affiche « Démarrage guidé : étape N, titre », avec « Revenir au
 *   guide » et « C'est fait » : on ne sort jamais du guide.
 */
export const CHEMIN_GUIDE = '/guide';
export const adresseDuGuide = (dossierId: string): string => `/dossier/demarrage?dossier=${encodeURIComponent(dossierId)}&guide=1`;

const carte: CSSProperties = { background: UI.fond, border: `1px solid #D9DBE7`, borderRadius: 16, padding: ESPACEMENTS.m, boxSizing: 'border-box' };
const boutonPrincipal: CSSProperties = {
  minHeight: 48,
  padding: '0 20px',
  borderRadius: 999,
  border: 0,
  background: UI.bouton,
  color: '#FFFFFF',
  font: 'inherit',
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const boutonSecondaire: CSSProperties = {
  ...boutonPrincipal,
  background: UI.fond,
  color: UI.marine,
  border: `2px solid ${UI.marine}`,
};
const rangee: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' };

function ChoixMode({ choisir }: { choisir: (m: ModeTravail) => void }) {
  const choix: CSSProperties = {
    ...carte,
    flex: '1 1 260px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    padding: ESPACEMENTS.l,
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    minHeight: 160,
  };
  return (
    <section
      data-choix-mode=""
      aria-labelledby="choix-mode-titre"
      style={{ ...carte, display: 'flex', flexDirection: 'column', gap: ESPACEMENTS.m, color: UI.texte, fontSize: 18, marginBottom: ESPACEMENTS.l, background: UI.fondDoux }}
    >
      <h2 id="choix-mode-titre" style={{ margin: 0, color: UI.marine, fontSize: 26 }}>
        Comment souhaitez-vous travailler ?
      </h2>
      <p style={{ margin: 0 }}>Vous pourrez changer à tout moment depuis le bandeau en haut de l’écran.</p>
      <div style={rangee}>
        <button type="button" style={{ ...choix, borderColor: UI.bouton, borderWidth: 2 }} onClick={() => choisir('guide')}>
          <span style={{ fontSize: 22, fontWeight: 700, color: UI.marine }}>Mode guidé</span>
          <span>Vos dossiers en cours et un grand bouton pour en créer un. L’outil vous pose les questions et enchaîne les étapes.</span>
        </button>
        <button type="button" style={choix} onClick={() => choisir('avance')}>
          <span style={{ fontSize: 22, fontWeight: 700, color: UI.marine }}>Mode avancé</span>
          <span>Toute l’application : pilotage, dossiers, documents, messages et réglages.</span>
        </button>
      </div>
    </section>
  );
}

function BandeauMode({ mode, basculer }: { mode: ModeTravail; basculer: () => void }) {
  return (
    <div data-bandeau-mode="" style={{ ...rangee, justifyContent: 'flex-end', fontSize: 16, color: UI.texteSecondaire, marginBottom: ESPACEMENTS.s }}>
      <span>
        Vous êtes en <strong style={{ color: UI.marine }}>{mode === 'guide' ? 'Mode guidé' : 'Mode avancé'}</strong>
      </span>
      <button type="button" style={{ ...boutonSecondaire, minHeight: 44, padding: '0 16px', borderWidth: 1 }} onClick={basculer}>
        {mode === 'guide' ? 'Passer en mode avancé' : 'Passer en mode guidé'}
      </button>
    </div>
  );
}

/** Bandeau d'un écran ouvert depuis le guide : l'étape, le retour au guide et « C'est fait » (jamais bloqué). */
export function BandeauGuide({ dossierId, codeEtape, role }: { dossierId: string; codeEtape: string | null; role: ReturnType<typeof roleEffectif> }) {
  const naviguer = useNaviguer();
  const controleur = useMemo(
    () =>
      creerControleurDemarrage({
        client: creerClient({ fetch: (...a) => globalThis.fetch(...a) }),
        dossierId,
        versionCatalogue: CATALOGUE_DEMARRAGE.version,
      }),
    [dossierId],
  );
  const etat = useSyncExternalStore(controleur.subscribe, controleur.getEtat, controleur.getEtat);
  useEffect(() => void controleur.charger(), [controleur]);
  const [avertissement, setAvertissement] = useState<{ conditions: string[]; leverMandat: boolean } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const etape = CATALOGUE_DEMARRAGE.etapes.find((e) => e.code === codeEtape) ?? null;
  const retour = adresseDuGuide(dossierId);

  const faire = async () => {
    if (!etape) return;
    const ligne = etat.avancement.find((a) => a.codeEtape === etape.code);
    // Une étape à question se répond dans le guide : on y retourne.
    if (etape.question && !ligne?.reponse) return naviguer(retour);
    const faits = {
      mandatRechercheSigne: etat.mandatRechercheSigne,
      forcages: etat.forcages,
      personnesArbre: etat.personnesArbre,
      cochees: ligne?.conditionsCochees ?? [],
    };
    const manque = manquantes(etape, optionChoisie(etape, ligne?.reponse), faits);
    if (manque.length > 0) {
      const leverMandat =
        manque.includes(CONDITION_MANDAT_RECHERCHE) ||
        evaluerConditions(etape, faits).some((c) => c.etat === 'non' && c.condition.evaluation === 'mandat_recherche_signe');
      setAvertissement({ conditions: manque, leverMandat });
      return;
    }
    if (await controleur.marquerFait(etape.code)) naviguer(retour);
    else setErreur('L’étape n’a pas été enregistrée. Vérifiez la connexion puis réessayez.');
  };

  return (
    <div
      data-bandeau-guide=""
      role="region"
      aria-label="Démarrage guidé"
      style={{ ...carte, background: UI.fondDoux, borderColor: UI.marine, borderWidth: 2, display: 'flex', flexDirection: 'column', gap: 8, marginBottom: ESPACEMENTS.m, color: UI.texte, fontSize: 16 }}
    >
      <p style={{ margin: 0, fontWeight: 700, color: UI.marine, fontSize: 18 }}>
        {etape ? `Démarrage guidé : étape ${etape.numero}, ${etape.titre}` : 'Démarrage guidé'}
      </p>
      {etape ? <p style={{ margin: 0 }}>{etape.quoiFaire}</p> : null}
      <div style={rangee}>
        <Lien vers={retour} style={boutonSecondaire} enfants="Revenir au guide" />
        {etape ? (
          <button type="button" style={boutonPrincipal} disabled={etat.statut !== 'pret' || etat.enCours !== null} onClick={() => void faire()}>
            C’est fait
          </button>
        ) : null}
      </div>
      {erreur ? (
        <p role="alert" style={{ margin: 0, color: UI.erreur }}>
          {erreur}
        </p>
      ) : null}
      {avertissement && etape ? (
        <AvertissementForcage
          conditions={avertissement.conditions}
          role={role}
          dirigeante={CATALOGUE_DEMARRAGE.intervenants.dirigeante}
          annuler={() => setAvertissement(null)}
          forcer={async (motif) => {
            const ok = await controleur.forcer(etape.code, avertissement.conditions, motif, avertissement.leverMandat);
            if (ok) naviguer(retour);
            return ok;
          }}
          prevenir={() => controleur.demanderDeblocage(etape.code, avertissement.conditions)}
        />
      ) : null}
    </div>
  );
}

const cheminSeul = (chemin: string): string => chemin.split('?')[0] ?? chemin;
const parametres = (chemin: string): URLSearchParams => new URLSearchParams(chemin.includes('?') ? chemin.slice(chemin.indexOf('?') + 1) : '');

function CadreGuideInterne({
  session,
  chemin,
  largeur,
  ecranId,
  children,
}: {
  session: Session;
  chemin: string;
  largeur: number;
  ecranId: string;
  children: ReactNode;
}) {
  const naviguer = useNaviguer();
  const role = roleEffectif(session);
  const userId = (session.effectiveUser ?? session.user).id;
  const mode = useModeTravail(userId, role);
  const accueil = accueilPour(role, largeur);
  const estAccueil = cheminSeul(chemin) === accueil;
  const p = parametres(chemin);
  const dossierGuide = p.get('guide') === '1' ? p.get('dossier') : null;

  useEffect(() => {
    if (mode.statut === 'lu' && mode.mode === 'guide' && estAccueil) naviguer(CHEMIN_GUIDE);
  }, [mode.statut, mode.mode, estAccueil, naviguer]);

  const choisir = (m: ModeTravail) =>
    void choisirModeTravail(m).then((ok) => {
      if (ok && m === 'guide') naviguer(CHEMIN_GUIDE);
      else if (ok && m === 'avance' && cheminSeul(chemin) === CHEMIN_GUIDE) naviguer(accueil);
    });

  // Le choix se pose EN TÊTE de l'accueil, sans le masquer : l'accueil habituel reste dessous tant qu'aucun mode n'est choisi.
  const aChoisir = mode.statut === 'lu' && mode.mode === null && estAccueil;
  return (
    <>
      {aChoisir ? <ChoixMode choisir={choisir} /> : null}
      {mode.statut === 'lu' && mode.mode !== null ? (
        <BandeauMode mode={mode.mode} basculer={() => choisir(mode.mode === 'guide' ? 'avance' : 'guide')} />
      ) : null}
      {dossierGuide && ecranId !== 'D17' && ecranId !== 'D18' ? <BandeauGuide dossierId={dossierGuide} codeEtape={p.get('etape')} role={role} /> : null}
      {children}
    </>
  );
}

/**
 * Enveloppe mémoïsée : un objet pour React, pas une fonction appelée telle quelle par les outils qui déplient l'arbre de la coquille
 * sans la monter (ses crochets ne s'exécutent qu'au vrai rendu).
 */
export const CadreGuide = memo(CadreGuideInterne);
